import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, Star, ArrowRight, Sparkles, Shield, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { businessInfo } from '../data/mock';

export const RedlandsCA = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Mobile Car Detailing in Redlands, CA | Precision Mobile Detailing';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional mobile car detailing in Redlands, CA. Serving University of Redlands, downtown, and all neighborhoods. Premium auto detailing at your location. Book today!');
    }

    // Add structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Precision Mobile Detailing - Redlands",
      "image": "https://customer-assets.emergentagent.com/job_shine-mobile-14/artifacts/48ukm1vz_image.png",
      "@id": "",
      "url": window.location.href,
      "telephone": businessInfo.phone,
      "priceRange": "$$",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Redlands",
        "addressRegion": "CA",
        "addressCountry": "US"
      },
      "areaServed": {
        "@type": "City",
        "name": "Redlands",
        "addressRegion": "CA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 34.0556,
        "longitude": -117.1825
      },
      "description": "Professional mobile car detailing service in Redlands, CA",
      "serviceType": ["Auto Detailing", "Car Detailing", "Mobile Detailing"]
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    { name: 'Full Detail Package', link: '/services' },
    { name: 'Interior Reset', link: '/services' },
    { name: 'Clay and Seal', link: '/services' },
    { name: 'Paint Enhancement', link: '/services' }
  ];

  const faqs = [
    {
      question: 'Do you provide mobile detailing throughout Redlands?',
      answer: 'Yes! We serve all of Redlands including the University of Redlands area, downtown, South Redlands, and surrounding neighborhoods. We come directly to your home, office, or any location you prefer.'
    },
    {
      question: 'How long does mobile car detailing take in Redlands?',
      answer: 'Most services take 2-6 hours depending on the package you choose and your vehicle size. We work efficiently at your location while you go about your day.'
    },
    {
      question: 'What do I need to provide for mobile detailing?',
      answer: 'We bring everything needed—water, power, and professional equipment. All we need is a safe place to park near your vehicle. We supply all products and handle everything.'
    },
    {
      question: 'Do you detail cars near the University of Redlands?',
      answer: 'Absolutely! We regularly service vehicles at and around the University of Redlands campus, student housing, and faculty residences. Mobile detailing is perfect for busy students and staff.'
    },
    {
      question: 'How do I book mobile detailing in Redlands, CA?',
      answer: 'You can book online through our website or call us directly. We offer flexible scheduling including weekends to fit your schedule.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 to-black text-white">
        <div className="absolute inset-0 bg-[url('https://dynamic-media-cdn.tripadvisor.com/media/photo-o/2b/17/14/48/caption.jpg?w=1200&h=-1&s=1')] bg-cover bg-center opacity-20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center gap-2 mb-6">
              <MapPin className="w-5 h-5" />
              <span className="text-sm font-semibold uppercase tracking-wider">Redlands, California</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Mobile Car Detailing in Redlands, CA
            </h1>
            
            <p className="text-xl mb-8 text-gray-300 leading-relaxed">
              Professional auto detailing brought directly to your driveway in Redlands. 
              Serving the University District, Historic Downtown, and all Redlands neighborhoods with premium mobile detailing services.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button asChild size="lg" className="bg-white text-black hover:bg-gray-100 text-lg px-8 py-6">
                <Link to="/booking">
                  Book Redlands Detailing <ArrowRight className="ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 border-white text-white hover:bg-white hover:text-black transition-all duration-300">
                <a href={`tel:${businessInfo.phone}`}>
                  <Phone className="mr-2" /> {businessInfo.phone}
                </a>
              </Button>
            </div>

            <div className="flex flex-wrap gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                <span>5.0 Rating • 30 Reviews</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span>{businessInfo.daysOpen} • {businessInfo.hours}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Mobile Detailing in Redlands */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Premium Mobile Auto Detailing Redlands Residents Trust</h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Looking for top-quality car detailing in Redlands without the hassle of driving to a shop? 
                Precision Mobile Detailing brings professional auto care directly to your location anywhere in Redlands, CA.
              </p>
              <p>
                Whether you're near the University of Redlands campus, in the historic downtown area, or anywhere across South Redlands, 
                our mobile detailing service saves you time while delivering showroom-quality results. We serve homes, apartments, 
                businesses, and job sites throughout the city.
              </p>
              <p>
                Our fully equipped mobile units carry everything needed for a complete detail—professional-grade products, 
                water, power equipment, and years of expertise. You get the same premium service as a high-end detailing shop, 
                but we come to you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">How Mobile Detailing Works in Redlands</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  1
                </div>
                <h3 className="text-xl font-bold mb-3">Book Online or Call</h3>
                <p className="text-gray-600">Choose your service package and schedule a time that works for you. We offer flexible appointments throughout Redlands.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  2
                </div>
                <h3 className="text-xl font-bold mb-3">We Come to You</h3>
                <p className="text-gray-600">Our team arrives at your Redlands location—home, work, or anywhere—fully equipped and ready to transform your vehicle.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  3
                </div>
                <h3 className="text-xl font-bold mb-3">Enjoy the Results</h3>
                <p className="text-gray-600">Relax while we work. When we're done, your car looks incredible—and you never had to leave home or wait at a shop.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services in Redlands */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">Car Detailing Services in Redlands, CA</h2>
            <p className="text-center text-gray-600 mb-12 text-lg">
              From quick refreshes to full transformations, we offer a range of mobile detailing packages for Redlands vehicles.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {services.map((service, idx) => (
                <Card key={idx} className="border hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <CheckCircle className="w-6 h-6 text-black flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="text-xl font-bold mb-2">{service.name}</h3>
                        <Link to={service.link} className="text-black font-semibold hover:underline">
                          View Details →
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <Button asChild size="lg" className="cta-button text-lg px-8 py-6">
                <Link to="/services">
                  View All Services & Pricing
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Why Redlands Chooses Precision Mobile Detailing</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4">Premium Products</h3>
                  <p className="text-gray-600">We use only professional-grade detailing products to protect and enhance your vehicle's finish.</p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4">Trusted & Reliable</h3>
                  <p className="text-gray-600">5.0-star rating from satisfied Redlands customers. Punctual, professional, and detail-obsessed.</p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <MapPin className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4">True Mobile Service</h3>
                  <p className="text-gray-600">We come to you anywhere in Redlands—home, office, campus, or job site. No driving required.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area within Redlands */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">Neighborhoods We Serve in Redlands</h2>
            <p className="text-center text-gray-600 mb-8 text-lg">
              We provide mobile car detailing throughout Redlands and nearby communities.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              {['University of Redlands', 'Downtown Redlands', 'South Redlands', 'Redlands Heights', 'Bryn Mawr', 'Smiley Park Area'].map((area, idx) => (
                <span key={idx} className="bg-gray-100 px-6 py-3 rounded-full font-semibold text-gray-700">
                  {area}
                </span>
              ))}
            </div>

            <p className="text-center text-gray-600 mt-8">
              Also proudly serving nearby <Link to="/service-areas/loma-linda-ca" className="text-black font-semibold hover:underline">Loma Linda</Link> and <Link to="/service-areas/yucaipa-ca" className="text-black font-semibold hover:underline">Yucaipa</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Frequently Asked Questions</h2>
            
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border shadow-sm">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-3">{faq.question}</h3>
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Book Mobile Detailing in Redlands Today</h2>
          <p className="text-xl mb-10 max-w-2xl mx-auto text-gray-300">
            Experience premium car detailing without leaving your driveway. Schedule your appointment now.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-black hover:bg-gray-100 text-lg px-8 py-6">
              <Link to="/booking">
                Book Now <ArrowRight className="ml-2" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 border-white text-white hover:bg-white hover:text-black transition-all duration-300">
              <Link to="/service-areas">
                View All Service Areas
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RedlandsCA;
